import React from "react";
import { cn } from "@/lib/utils";
import { anchorSegments } from "@/components/reader/readingEngine";

// Generic, memoised reading surface. Renders the whole document ONCE as
// index-addressable tokens (data-tok = global word index) with static Smart
// Highlight classes and optional Word Anchor letters. The moving reading guide
// (active word/sentence) is applied imperatively by the parent stage via class
// toggles, so the page re-renders only when the document, highlight layers,
// anchor mode or typography change — never on every guide step. This keeps the
// guide smooth on long documents.

function renderToken(text, anchorMode) {
    const seg = anchorMode && anchorMode !== "off" ? anchorSegments(text, anchorMode) : null;
    if (!seg) return `${text} `;
    return (
        <>
            <b className="rg-anchor">{seg.head}</b>
            {seg.mid}
            {seg.last && <b className="rg-anchor">{seg.last}</b>}
            {" "}
        </>
    );
}

function ReadingSurfaceBase({
    model,
    surfaceRef,
    highlightIndex,
    enableHighlights = false,
    dim = false,
    anchorMode = "off",
    anchorIntensity = 0.8,
    fontFamily,
    fontSize,
    maxWidth,
    lineHeight,
    textAlign = "left",
    onTokenClick,
    onExplain,
    className,
    headLayer = false,
    headRef,
    testid = "reading-surface",
}) {
    const style = {
        fontFamily,
        fontSize,
        lineHeight,
        maxWidth: maxWidth || "58ch",
        textAlign,
        "--rg-anchor-op": anchorIntensity,
    };
    return (
        <div
            ref={surfaceRef}
            data-testid={testid}
            style={style}
            className={cn(
                "rg-surface mx-auto",
                dim && "rg-dim",
                (onTokenClick || onExplain) && "rg-clickable",
                className
            )}
        >
            {headLayer && (
                <div className="rg-headlayer" aria-hidden="true">
                    <span ref={headRef} className="rg-head" />
                </div>
            )}
            {model.paragraphs.map((para) => (
                <p key={para.index} className="mb-4">
                    {para.tokens.map((tok) => {
                        const cat = enableHighlights && highlightIndex ? highlightIndex.get(tok.i) : undefined;
                        const explainable = !!(cat && onExplain);
                        const handleClick = (explainable || onTokenClick)
                            ? (e) => {
                                if (explainable) { e.stopPropagation(); onExplain(tok.i, e); }
                                else onTokenClick(tok.i, e);
                            }
                            : undefined;
                        return (
                            <span
                                key={tok.i}
                                data-tok={tok.i}
                                onClick={handleClick}
                                className={cn("rg-tok", cat && `rg-hl rg-hl-${cat}`, explainable && "rg-explainable")}
                            >
                                {renderToken(tok.text, anchorMode)}
                            </span>
                        );
                    })}
                </p>
            ))}
        </div>
    );
}

export const ReadingSurface = React.memo(ReadingSurfaceBase);
